import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {DeprecatedScrollbar as Scrollbar, PopoverHandler} from '@forecast-it/design-system';

import {isTimeRegistrationNoteInvalid} from '../../../../../shared/util/time-registration/time-registration-settings/TimeRegistrationNoteFilter';
import {workingHourForTheDay} from '../../../timesheets_person_data';
import {TimeRegistration} from '../TimeRegistrationType';
import AdvancedTimeRegistrationContent, {PopupFocusItem, TimeRegistrationFocusType} from './AdvancedTimeRegistrationContent';
import {CancelButton, Column, Popup, PrimaryButton as UpdateButton, Row} from './AdvancedTimeRegistrationStyled';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {Role} from '../../../../../shared/components/modals/time-registration/time-registration-modal/types/TimeRegistrationRole';
import Moment from 'moment/moment';
import {useTaskEstimateValidation} from '../../../../../shared/util/time-registration/time-registration-settings/useTaskEstimateValidation';
import {useIntl} from 'react-intl';
import {isBillableSplitAllowed} from '../../../../../shared/util/cache/TimeRegistrationSettingsUtil';
import Spinner from '../../../../../shared/components/spinner/Spinner';
import {useSpinner} from '../../../../../shared/components/spinner/useSpinner';

type UpdatableProperties = Omit<Partial<TimeRegistration>, 'id'>;

function updateTimeRegistrationById(
	id: string,
	updatedProperties: UpdatableProperties,
	timeRegistrations: TimeRegistration[]
): TimeRegistration[] {
	return timeRegistrations.map(timeRegistration => {
		if (timeRegistration.id === id) {
			// Set a changed flag in order to only run mutations on the changed time registrations in the end
			return {...timeRegistration, ...updatedProperties, shouldUpdate: true};
		}
		return timeRegistration;
	});
}

function getInitialTimeRegistrationList(timeRegistrationsOnEntityOnDay: TimeRegistration[]): TimeRegistration[] {
	return timeRegistrationsOnEntityOnDay
		.filter(timeRegistration => {
			return timeRegistration.fullAccessToProject;
		})
		.map(timeRegistration => ({
			...timeRegistration,
			initialMinutesRegistered: timeRegistration.minutesRegistered,
			billableMinutesRegistered:
				timeRegistration.minutesRegistered !== timeRegistration.billableMinutesRegistered
					? timeRegistration.billableMinutesRegistered
					: null,
			noteInvalid: false,
			shouldUpdate: false,
		}));
}

function areAnyBillable(timeRegNodes: TimeRegistration[]) {
	return timeRegNodes.some(timeReg => timeReg.billable);
}

function canAnyHaveRole(timeRegNodes: TimeRegistration[]) {
	return timeRegNodes.some(timeReg => timeReg.canHaveRole);
}

type UpdateAdvancedTimeRegistrationProps = {
	timeRegistrationsOnEntityOnDay: TimeRegistration[];
	selectedPerson: Record<string, unknown>;
	dayOfInput: Date;
	mutation: (
		hours: number | undefined,
		billableHours: number | null | undefined,
		notes: string,
		id: string,
		roleId: string | undefined,
		onSuccess: () => void
	) => void;
	projectId?: string;
	isCompanyTimesheet?: boolean;
};

function getTimeRegistrationsToUpdate(timeRegistrations: TimeRegistration[]) {
	return timeRegistrations.filter(timeRegistration => !!timeRegistration.shouldUpdate);
}

function hasTimeRegistrationsToUpdate(timeRegistrations: TimeRegistration[]) {
	return getTimeRegistrationsToUpdate(timeRegistrations).length > 0;
}

function UpdateAdvancedTimeRegistrationContent({
	timeRegistrationsOnEntityOnDay,
	selectedPerson,
	dayOfInput,
	mutation,
	projectId,
	isCompanyTimesheet,
}: UpdateAdvancedTimeRegistrationProps): JSX.Element {
	useEffect(() => {
		trackEvent('Update Advanced Time Registration Popup', 'Opened');
	}, []);

	const intl = useIntl();
	const {setOpen} = PopoverHandler.usePopoverState();
	const [waitingForMutation, setWaitingForMutation] = useState(false);
	const {spinning} = useSpinner({waiting: waitingForMutation});

	const [timeRegistrations, setTimeRegistrations] = useState<TimeRegistration[]>(
		getInitialTimeRegistrationList(timeRegistrationsOnEntityOnDay)
	);
	const [timeRegistrationFocus, setTimeRegistrationFocus] = useState<TimeRegistrationFocusType>({
		id: null,
		focusItem: null,
	}); // Used to propagate focus control down to children

	const updateButtonRef = useRef<HTMLButtonElement>(null);

	const onTimeChange = useCallback(
		(id: string, minutes: number, isEnterBlur?: boolean) => {
			setTimeRegistrations(updateTimeRegistrationById(id, {minutesRegistered: minutes}, timeRegistrations));
			if (isEnterBlur) {
				updateButtonRef.current?.focus();
			}
		},
		[timeRegistrations]
	);

	const onBillableTimeChange = useCallback(
		(id: string, minutes: number | null, isEnterBlur?: boolean) => {
			setTimeRegistrations(updateTimeRegistrationById(id, {billableMinutesRegistered: minutes}, timeRegistrations));
			if (isEnterBlur) {
				updateButtonRef.current?.focus();
			}
		},
		[timeRegistrations]
	);

	const onNotesChange = useCallback(
		(id: string, notes: string) => {
			setTimeRegistrations(updateTimeRegistrationById(id, {notes: notes, noteInvalid: false}, timeRegistrations));
		},
		[timeRegistrations]
	);

	const onRoleChange = useCallback(
		(id: string, role: Role) => {
			setTimeRegistrations(updateTimeRegistrationById(id, {role: role}, timeRegistrations));
		},
		[timeRegistrations]
	);

	function handleCancel() {
		setTimeRegistrations(getInitialTimeRegistrationList(timeRegistrationsOnEntityOnDay));
		setOpen(false);
		trackEvent('Update Advanced Time Registration Popup', 'Closed');
	}

	function updateTimeRegistrations(timeRegistrations: TimeRegistration[]) {
		setWaitingForMutation(true);

		const timeRegistrationsToUpdate = getTimeRegistrationsToUpdate(timeRegistrations);

		const totalNewMinutesRegisteredByTaskId: {[key: string]: number} = {};
		let firstInvalidMinutesTimeReg: TimeRegistration | undefined = undefined;
		for (const timeRegistration of timeRegistrationsToUpdate) {
			const taskId = timeRegistration.taskId || 'noTask';
			const {minutesRegistered, initialMinutesRegistered, taskTotalMinutesRegistered, taskEstimateForecastMinutes} =
				timeRegistration;

			/*
			 * We don't use the initialMinutes here as we want the totalNewMinutesRegisteredByTaskId to account for new values being lower than initial
			 * We can have multiple timeregs for the same task in the list so if you lower the first registration you should be able to increase the subsequent ones
			 **/
			const {validateTimeInputValue} = useTaskEstimateValidation(
				taskEstimateForecastMinutes,
				taskTotalMinutesRegistered,
				0,
				intl
			);

			if (!totalNewMinutesRegisteredByTaskId[taskId]) {
				totalNewMinutesRegisteredByTaskId[taskId] = 0;
			}
			totalNewMinutesRegisteredByTaskId[taskId] += initialMinutesRegistered
				? minutesRegistered - initialMinutesRegistered
				: minutesRegistered;

			timeRegistration.timeError = validateTimeInputValue(totalNewMinutesRegisteredByTaskId[taskId]);
			if (timeRegistration.timeError) {
				firstInvalidMinutesTimeReg = timeRegistration;
				break;
			}
		}

		if (firstInvalidMinutesTimeReg) {
			setWaitingForMutation(false);
			setTimeRegistrationFocus({id: firstInvalidMinutesTimeReg.id, focusItem: PopupFocusItem.Hours});
			setTimeRegistrations(
				timeRegistrations.map(timeRegistration => ({
					...timeRegistration,
				}))
			);
			return;
		}

		// In case time registrations exist from before the setting was enabled without notes, we only want to require a note in case the time registration itself is being changed.
		const firstInvalidNoteTimeReg = timeRegistrationsToUpdate.find(timeRegistration =>
			isTimeRegistrationNoteInvalid(timeRegistration.notes)
		);

		if (firstInvalidNoteTimeReg) {
			setWaitingForMutation(false);
			setTimeRegistrationFocus({id: firstInvalidNoteTimeReg.id, focusItem: PopupFocusItem.Notes});
			setTimeRegistrations(
				timeRegistrations.map(timeRegistration => ({
					...timeRegistration,
					noteInvalid: timeRegistration.shouldUpdate ? isTimeRegistrationNoteInvalid(timeRegistration.notes) : false,
				}))
			);
			return;
		}

		function onSuccess(timeRegistration: TimeRegistration) {
			setWaitingForMutation(false);
			setOpen(false);
			trackEvent('Advanced Time Registration', 'Updated', {
				initialValues: getInitialTimeRegistrationList(timeRegistrationsOnEntityOnDay).find(
					t => t.id === timeRegistration.id
				),
				updatedValues: timeRegistration,
			});
		}

		timeRegistrationsToUpdate.forEach(timeRegistration => {
			const billable = timeRegistration.billable;
			const hoursRegistered = timeRegistration.minutesRegistered ? timeRegistration.minutesRegistered / 60 : 0;
			const billableHoursRegistered = billable
				? timeRegistration.billableMinutesRegistered != null
					? timeRegistration.billableMinutesRegistered / 60
					: null
				: undefined;
			mutation(
				hoursRegistered,
				billableHoursRegistered,
				timeRegistration.notes,
				timeRegistration.id,
				timeRegistration.role?.id,
				() => onSuccess(timeRegistration)
			);
		});
	}

	const workingHours = useMemo(() => workingHourForTheDay(selectedPerson, Moment(dayOfInput)), [selectedPerson, dayOfInput]);

	const makeWider = areAnyBillable(timeRegistrations) && isBillableSplitAllowed() && canAnyHaveRole(timeRegistrations);

	function canUpdate(timeRegistrations: TimeRegistration[]) {
		return hasTimeRegistrationsToUpdate(timeRegistrations) && !waitingForMutation;
	}

	return (
		<Popup makeWider={makeWider}>
			<Scrollbar maxHeight={'270px'}>
				<Column>
					{timeRegistrations.map(timeRegistration => {
						const {
							id,
							minutesRegistered,
							billableMinutesRegistered,
							billable,
							notes,
							noteInvalid,
							role,
							timeError,
							initialMinutesRegistered,
							taskTotalMinutesRegistered,
							taskEstimateForecastMinutes,
							canHaveRole,
							isTimeRegLocked,
							tooltipMessage,
						} = timeRegistration;

						const {remainingInfoMessage} = useTaskEstimateValidation(
							taskEstimateForecastMinutes,
							taskTotalMinutesRegistered,
							initialMinutesRegistered,
							intl
						);
						return (
							<AdvancedTimeRegistrationContent
								id={id}
								key={id}
								timeInputMinutes={minutesRegistered}
								onTimeChange={(minutes, isEnterBlur) => onTimeChange(id, minutes, isEnterBlur)}
								billableTimeInputMinutes={billableMinutesRegistered}
								onBillableTimeChange={(minutes, isEnterBlur) => onBillableTimeChange(id, minutes, isEnterBlur)}
								notes={notes}
								onNotesChange={notes => onNotesChange(id, notes)}
								billable={billable}
								noteError={noteInvalid}
								timeError={timeError}
								remainingInfoMessage={remainingInfoMessage}
								workingHours={workingHours}
								timeRegistrationFocus={timeRegistrationFocus}
								role={role}
								onRoleChange={role => onRoleChange(id, role)}
								firstTimeRegistration={timeRegistration === timeRegistrations[0]}
								canHaveRole={canHaveRole}
								projectId={projectId}
								isCompanyTimesheet={isCompanyTimesheet}
								isTimeRegLocked={isTimeRegLocked}
								tooltipMessage={tooltipMessage}
							/>
						);
					})}
				</Column>
			</Scrollbar>

			<Row style={{justifyContent: 'flex-end'}}>
				<CancelButton disabled={waitingForMutation} onClick={handleCancel} z-index={10}>
					Cancel
				</CancelButton>
				<UpdateButton
					z-index={11}
					ref={updateButtonRef}
					data-cy="update-time-entry-button"
					onClick={() => updateTimeRegistrations(timeRegistrations)}
					disabled={!canUpdate(timeRegistrations)}
				>
					<Spinner children={'Update'} spinning={spinning} />
				</UpdateButton>
			</Row>
		</Popup>
	);
}

export default UpdateAdvancedTimeRegistrationContent;
